/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * ConditionLabeler.js  —  TEMPORARY internal tool (admin dash).
 *
 * Serves batches of sold + analyzed + unlabeled properties and lets a human
 * score condition room-by-room (1-5), viewing that room's photos. Writes only
 * to Property.conditionLabels via /api/admin/labeling/submit.
 *
 * Mount it in the admin dash like any other admin page/tab. Delete when
 * labeling is complete.
 *
 * Labeling discipline (see spec): score each room 1=dated .. 5=luxury using
 * agreed anchors. If a room isn't visible, leave it "Can't see" (stored null) —
 * NEVER guess. Keep founder + cofounder consistent.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, SkipForward, Check, AlertCircle, Search, Square } from 'react-feather';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

// Palette (matches the agent portal light-mode refresh)
const C = {
  bg: '#fcfcfc',
  panel: '#eeebe3',
  accent: '#fdd700',
  ink: '#1a1a1a',
  sage: '#7a9471',
  border: '#e2ddd2',
  muted: '#8a8578'
};

// The four scored rooms + display labels. Order = labeling flow.
const ROOMS = [
  { key: 'kitchenCondition',  label: 'Kitchen' },
  { key: 'bathCondition',     label: 'Primary Bath' },
  { key: 'exteriorCondition', label: 'Exterior' },
  { key: 'livingCondition',   label: 'Living / Main' }
];

// 1-5 anchors shown as a legend so scoring stays consistent.
const ANCHORS = [
  { v: 1, t: 'Dated' },
  { v: 2, t: 'Builder-grade' },
  { v: 3, t: 'Updated' },
  { v: 4, t: 'Renovated' },
  { v: 5, t: 'Luxury' }
];

// LauftQ axes (0-100). Labels show the two poles so the reviewer reads the
// scale direction correctly. Low end (left, 0) = first word; high end
// (right, 100) = second word. Confirmed against app.py: score is driven by the
// "high" class probability, and axis name order is low-vs-high.
const LAUFTQ_AXES = [
  { key: 'dynamicVsCalm',       low: 'Dynamic',  high: 'Calm' },
  { key: 'modernVsTraditional', low: 'Modern',   high: 'Traditional' },
  { key: 'socialVsPrivate',     low: 'Social',   high: 'Private' },
  { key: 'naturalVsEngineered', low: 'Natural',  high: 'Engineered' }
];

// Five discrete buckets across the TRUE 0-100 range. Humans get the full range
// even though the current model only outputs ~25-75 — that's deliberate, so the
// labels can teach a future retrain the extremes the model can't yet produce.
const DOT_VALUES = [0, 25, 50, 75, 100];

// Snap any value (e.g. the model's 25-75 score) to the nearest of the 5 dots.
function snapToDot(v) {
  if (v == null) return 50;
  let best = DOT_VALUES[0];
  let bestDist = Math.abs(v - best);
  for (const d of DOT_VALUES) {
    const dist = Math.abs(v - d);
    if (dist < bestDist) { best = d; bestDist = dist; }
  }
  return best;
}

const ConditionLabeler = () => {
  const [batch, setBatch] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [idx, setIdx] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [labeledThisSession, setLabeledThisSession] = useState(0);

  // Current property's working scores. null = "can't see / not scored".
  const [scores, setScores] = useState({});
  const [overall, setOverall] = useState(null);
  const [note, setNote] = useState('');
  // LauftQ review working state: { axis: { value, status } } where status is
  // 'confirmed' | 'corrected'. Seeded from the model's values on load.
  const [lq, setLq] = useState({});
  // Which labeler — set once, sent with each submit.
  const [labeledBy, setLabeledBy] = useState('');

  // ----- sold-scan state (logic defined after fetchBatch below) -----
  const [scan, setScan] = useState(null);     // { status, scanned, found, target, scanCap }
  const [scanPolling, setScanPolling] = useState(false);

  // ----- condition auto-assign state -----
  const [autoCount, setAutoCount] = useState(100);
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoMsg, setAutoMsg] = useState('');

  const current = batch[idx] || null;

  // Seed per-room LauftQ working state whenever the property changes. For each
  // room found, take its representative (top-confidence) image and seed 4
  // sliders from THAT image's per-image personalityScores. Untouched = confirmed.
  useEffect(() => {
    if (current && current.photosByRoom) {
      const seed = {};
      for (const room of ROOMS) {
        const photos = current.photosByRoom[room.key] || [];
        if (!photos.length) continue;          // room not found → no LauftQ entry
        const rep = photos[0];                 // top-confidence representative
        const ps = rep.personalityScores || {};
        seed[room.key] = {
          imageUrl: rep.url,
          roomType: rep.roomType,
          axes: {
            dynamicVsCalm:       { value: snapToDot(ps.dynamicVsCalm), status: 'confirmed' },
            modernVsTraditional: { value: snapToDot(ps.modernVsTraditional), status: 'confirmed' },
            socialVsPrivate:     { value: snapToDot(ps.socialVsPrivate), status: 'confirmed' },
            naturalVsEngineered: { value: snapToDot(ps.naturalVsEngineered), status: 'confirmed' }
          }
        };
      }
      setLq(seed);
    } else {
      setLq({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current && current.id]);

  const resetWorking = () => {
    setScores({});
    setOverall(null);
    setNote('');
    // NOTE: do NOT setLq({}) here. The seeding effect (keyed on property id) is
    // the single source of truth for lq and reseeds on every property change.
    // Blanking it here races the effect and was clobbering the seed (→ 0 rooms).
  };

  const fetchBatch = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/queue?batch=100`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` }
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load');
      setBatch(json.data.batch);
      setRemaining(json.data.remaining);
      setIdx(0);
      resetWorking();
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchBatch(); }, [fetchBatch]);

  // ----- sold-scan logic (placed after fetchBatch so the poll effect can use it) -----
  const startScan = async () => {
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/scan/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ target: 100, scanCap: 3000 })
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Could not start scan');
      setScan(json.data);
      setScanPolling(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const stopScan = async () => {
    try {
      await fetch(`${API_URL}/admin/labeling/scan/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${AuthService.getToken()}` }
      });
      // status poll will pick up the 'stopped' state; everything found is kept.
    } catch (e) {
      setError(e.message);
    }
  };

  // Kick off model condition auto-assign on analyzed properties (writes
  // conditionAnalysis, never conditionLabels). Runs in the background on the
  // server; we just fire it and show the accepted message.
  const runAutoAssign = async () => {
    setAutoRunning(true);
    setAutoMsg('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/auto-assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ count: Number(autoCount) || 100 })
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Auto-assign failed to start');
      setAutoMsg(
        json.scored === 0
          ? (json.message || 'Nothing left to score.')
          : `Started on ${json.batchSize} properties (${json.totalRemaining} remain). Email report when done.`
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setAutoRunning(false);
    }
  };

  useEffect(() => {
    if (!scanPolling) return undefined;
    const iv = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/admin/labeling/scan/status`, {
          headers: { Authorization: `Bearer ${AuthService.getToken()}` }
        });
        const json = await res.json();
        if (json.success && json.data) {
          setScan(json.data);
          if (json.data.status !== 'running') {
            setScanPolling(false);
            // scan ended (done / stopped / error) — reload the sold queue so the
            // freshly-flagged properties appear for labeling.
            fetchBatch();
          }
        }
      } catch (e) {
        // transient poll error — keep polling
      }
    }, 2500);
    return () => clearInterval(iv);
  }, [scanPolling, fetchBatch]);

  const setRoomScore = (key, value) => {
    setScores((prev) => ({ ...prev, [key]: value }));
  };

  const advance = () => {
    if (idx + 1 < batch.length) {
      setIdx(idx + 1);
      resetWorking();
    } else {
      // batch exhausted — pull the next 100
      fetchBatch();
    }
  };

  const submit = async () => {
    if (!current) return;
    setIsSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          propertyId: current.id,
          scores,                 // missing/null keys = "can't see", stored null
          overallImpression: overall,
          note,
          labeledBy: labeledBy || undefined
          // LauftQ review parked for now — intentionally NOT sending lauftqByRoom
          // so nothing is written to humanLauftQ. We'll relabel LauftQ cleanly
          // later; collecting rubber-stamped model values now would poison that.
        })
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Save failed');
      setLabeledThisSession((n) => n + 1);
      advance();
    } catch (e) {
      setError(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const skip = () => advance();

  // ----- scan bar (shown in both empty state and main view) -----
  const isScanning = scan && scan.status === 'running';
  const renderScanBar = () => (
    <div style={S.scanBar}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {!isScanning ? (
          <button style={S.btnPrimary} onClick={startScan}>
            <Search size={15} /> Find next 100 sold
          </button>
        ) : (
          <button style={S.btnStop} onClick={stopScan}>
            <Square size={14} /> Stop &amp; keep found
          </button>
        )}
        {scan && (
          <span style={{ color: C.muted, fontSize: 13 }}>
            {isScanning ? 'Scanning…' : scanLabel(scan.status)} ·{' '}
            found <strong style={{ color: C.sage }}>{scan.found}</strong>/{scan.target}
            {scan.candidatePool != null ? ` · ${scan.candidatePool} sold candidates` : ''}
            {scan.apiCalls != null ? ` · ${scan.apiCalls} API calls` : ''}
          </span>
        )}
      </div>
      {isScanning && (
        <div style={S.progressTrack}>
          <div style={{ ...S.progressFill, width: `${Math.min(100, (scan.found / scan.target) * 100)}%` }} />
        </div>
      )}

      {/* Condition auto-assign: run the model over analyzed props, write
          conditionAnalysis (model current-state) — does NOT touch human labels. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
        <span style={{ color: C.muted, fontSize: 13 }}>Auto-assign condition to</span>
        <input
          type="number"
          min={1}
          max={1000}
          value={autoCount}
          onChange={(e) => setAutoCount(e.target.value)}
          style={{ width: 80, padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, color: C.ink }}
        />
        <span style={{ color: C.muted, fontSize: 13 }}>properties (max 1000)</span>
        <button
          style={autoRunning ? S.btnGhost : S.btnPrimary}
          onClick={runAutoAssign}
          disabled={autoRunning}
        >
          {autoRunning ? 'Starting…' : 'Run model'}
        </button>
        {autoMsg && <span style={{ color: C.sage, fontSize: 13 }}>{autoMsg}</span>}
      </div>
    </div>
  );

  // ---------- render ----------
  if (isLoading && !batch.length) {
    return <div style={S.wrap}><div style={S.center}>Loading properties…</div></div>;
  }

  if (!current) {
    return (
      <div style={S.wrap}>
        {renderScanBar()}
        <div style={S.center}>
          <Check size={32} color={C.sage} />
          <p style={{ marginTop: 12 }}>No sold properties queued to label.</p>
          <p style={{ color: C.muted, fontSize: 13 }}>
            Run a scan to find more sold properties, or check again.
          </p>
          <button style={S.btnGhost} onClick={fetchBatch}>Check again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      {/* Header / progress */}
      <div style={S.header}>
        <div>
          <h2 style={{ margin: 0, color: C.ink }}>Condition Labeling</h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
            {remaining.toLocaleString()} remaining · {labeledThisSession} labeled this session ·
            property {idx + 1} of {batch.length} in batch
          </div>
        </div>
        <input
          style={S.byInput}
          placeholder="Labeler"
          value={labeledBy}
          onChange={(e) => setLabeledBy(e.target.value)}
        />
      </div>

      {renderScanBar()}

      {error && (
        <div style={S.errorBar}><AlertCircle size={16} /> {error}</div>
      )}

      {/* Property summary */}
      <div style={S.propBar}>
        <div>
          <strong style={{ color: C.ink }}>{current.address}</strong>
          <span style={{ color: C.muted }}> · {current.csz}</span>
        </div>
        <div style={{ color: C.muted, fontSize: 13 }}>
          {current.beds}bd / {current.baths}ba · {current.sqft ? current.sqft.toLocaleString() + ' sqft' : 'sqft n/a'}
          {current.soldPrice ? ` · sold $${Number(current.soldPrice).toLocaleString()}` : ''}
          {` · vision ${current.visionVersion || '?'} (${current.totalImagesAnalyzed} photos)`}
        </div>
      </div>

      {/* Anchor legend */}
      <div style={S.legend}>
        {ANCHORS.map((a) => (
          <span key={a.v} style={S.legendItem}>
            <strong>{a.v}</strong> {a.t}
          </span>
        ))}
      </div>

      {/* Per-room scoring */}
      {ROOMS.map((room) => {
        const photos = (current.photosByRoom && current.photosByRoom[room.key]) || [];
        const hasPhotos = photos.length > 0;
        const val = scores[room.key];
        return (
          <div key={room.key} style={S.roomBlock}>
            <div style={S.roomHead}>
              <span style={{ fontWeight: 600, color: C.ink }}>{room.label}</span>
              <span style={{ color: C.muted, fontSize: 12 }}>
                {hasPhotos ? `${photos.length} photo${photos.length > 1 ? 's' : ''}` : 'no photos'}
              </span>
            </div>

            {/* Photo strip for this room */}
            {hasPhotos ? (
              <div style={S.photoStrip}>
                {photos.map((ph, i) => (
                  <div key={i} style={S.photoWrap}>
                    <img src={ph.url} alt={room.label} style={S.photo} />
                    {ph.confidence != null && ph.confidence < 0.5 && (
                      <span style={S.lowConf} title="Low room-classification confidence">?</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={S.noPhotos}>No {room.label.toLowerCase()} photo — leave as "Can't see".</div>
            )}

            {/* 1-5 buttons + Can't see */}
            <div style={S.scoreRow}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  disabled={!hasPhotos}
                  onClick={() => setRoomScore(room.key, n)}
                  style={{
                    ...S.scoreBtn,
                    ...(val === n ? S.scoreBtnActive : {}),
                    ...(!hasPhotos ? S.scoreBtnDisabled : {})
                  }}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => setRoomScore(room.key, null)}
                style={{
                  ...S.cantSee,
                  ...(val === null && room.key in scores ? S.cantSeeActive : {})
                }}
              >
                Can't see
              </button>
            </div>
          </div>
        );
      })}

      {/* ===== LauftQ review — PARKED for now. Hidden via {false && ...} so we
           collect ZERO LauftQ labels until we relabel cleanly later. Code kept
           intact; flip to {true && ...} (or remove the guard) to re-enable. ===== */}
      {false && (
      <div style={S.lqSection}>
        <div style={S.roomHead}>
          <span style={{ fontWeight: 600, color: C.ink }}>LauftQ review (per room — retrains the classifier)</span>
          <span style={{ color: C.muted, fontSize: 12 }}>confirm if right, drag to correct</span>
        </div>
        <p style={{ color: C.muted, fontSize: 12, marginTop: 0, marginBottom: 14 }}>
          Separate judgment from condition — this is the room's <em>style/personality</em>, scored against
          the exact image the classifier used. Leaving a dot = confirming the model. Picking another = correcting.
          Leftmost = fully the left label, rightmost = fully the right label.
        </p>

        {ROOMS.map((room) => {
          const entry = lq[room.key];
          if (!entry) return null; // room not found for this property
          return (
            <div key={room.key} style={S.lqRoomBlock}>
              <div style={S.lqRoomHead}>
                <span style={{ fontWeight: 600, color: C.ink }}>{room.label}</span>
                <span style={{ color: C.muted, fontSize: 11 }}>{entry.roomType}</span>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <img src={entry.imageUrl} alt={room.label} style={S.lqThumb} />
                <div style={{ flex: 1 }}>
                  {LAUFTQ_AXES.map((axis) => {
                    const a = (entry.axes && entry.axes[axis.key]) || { value: 50, status: 'confirmed' };
                    return (
                      <div key={axis.key} style={S.lqAxis}>
                        <div style={S.lqLabels}>
                          <span>{axis.low}</span>
                          <span style={{
                            fontSize: 11,
                            color: a.status === 'corrected' ? C.ink : C.muted,
                            fontWeight: a.status === 'corrected' ? 700 : 400
                          }}>
                            {a.value}{a.status === 'corrected' ? ' (corrected)' : ''}
                          </span>
                          <span>{axis.high}</span>
                        </div>
                        <div style={S.dotRow}>
                          {DOT_VALUES.map((dv) => {
                            const selected = a.value === dv;
                            return (
                              <button
                                key={dv}
                                onClick={() => {
                                  setLq((prev) => ({
                                    ...prev,
                                    [room.key]: {
                                      ...prev[room.key],
                                      axes: {
                                        ...prev[room.key].axes,
                                        [axis.key]: { value: dv, status: 'corrected' }
                                      }
                                    }
                                  }));
                                }}
                                title={`${dv}`}
                                style={{ ...S.dot, ...(selected ? S.dotSelected : {}) }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Overall gut-check (not a model feature) + note */}
      <div style={S.roomBlock}>
        <div style={S.roomHead}>
          <span style={{ fontWeight: 600, color: C.ink }}>Overall impression</span>
          <span style={{ color: C.muted, fontSize: 12 }}>gut-check only — not used by the model</span>
        </div>
        <div style={S.scoreRow}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setOverall(n)}
              style={{ ...S.scoreBtn, ...(overall === n ? S.scoreBtnActive : {}) }}
            >
              {n}
            </button>
          ))}
        </div>
        <input
          style={S.note}
          placeholder="Note (optional) — e.g. 'mid-reno, kitchen done, baths not'"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {/* Actions */}
      <div style={S.actions}>
        <button style={S.btnGhost} onClick={skip} disabled={isSaving}>
          <SkipForward size={15} /> Skip
        </button>
        <button style={S.btnPrimary} onClick={submit} disabled={isSaving}>
          {isSaving ? 'Saving…' : <>Submit &amp; next <ChevronRight size={16} /></>}
        </button>
      </div>
    </div>
  );
};

// ---------- styles ----------
const S = {
  wrap: { maxWidth: 880, margin: '0 auto', padding: 20, background: C.bg, fontFamily: 'system-ui, sans-serif' },
  center: { textAlign: 'center', padding: 60, color: C.muted },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  byInput: { padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, background: '#fff' },
  scanBar: { background: C.panel, borderRadius: 10, padding: '12px 16px', marginBottom: 12 },
  btnStop: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 8, border: 'none', background: '#a33', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  progressTrack: { marginTop: 10, height: 6, borderRadius: 4, background: '#fff', overflow: 'hidden' },
  progressFill: { height: '100%', background: C.sage, transition: 'width 0.4s ease' },
  errorBar: { display: 'flex', alignItems: 'center', gap: 8, background: '#fde8e8', color: '#a33', padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 14 },
  propBar: { background: C.panel, borderRadius: 10, padding: '12px 16px', marginBottom: 12 },
  legend: { display: 'flex', gap: 14, flexWrap: 'wrap', padding: '8px 4px', marginBottom: 8, fontSize: 12, color: C.muted },
  legendItem: { whiteSpace: 'nowrap' },
  roomBlock: { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 12 },
  lqSection: { background: '#f3f1ea', border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 12 },
  lqRoomBlock: { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 10 },
  lqRoomHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  lqThumb: { width: 120, height: 90, objectFit: 'cover', borderRadius: 8, flex: '0 0 auto' },
  lqAxis: { marginBottom: 10 },
  lqLabels: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: C.ink, marginBottom: 3 },
  dotRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 },
  dot: { flex: 1, height: 22, borderRadius: 11, border: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer', padding: 0 },
  dotSelected: { background: C.sage, borderColor: C.sage },
  roomHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  photoStrip: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 12 },
  photoWrap: { position: 'relative', flex: '0 0 auto' },
  photo: { height: 140, width: 'auto', borderRadius: 8, objectFit: 'cover', display: 'block' },
  lowConf: { position: 'absolute', top: 6, right: 6, background: C.accent, color: C.ink, borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 },
  noPhotos: { color: C.muted, fontSize: 13, fontStyle: 'italic', marginBottom: 12 },
  scoreRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  scoreBtn: { width: 44, height: 44, borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.ink, fontSize: 16, fontWeight: 600, cursor: 'pointer' },
  scoreBtnActive: { background: C.accent, borderColor: C.accent },
  scoreBtnDisabled: { opacity: 0.35, cursor: 'not-allowed' },
  cantSee: { marginLeft: 6, padding: '0 14px', height: 44, borderRadius: 8, border: `1px dashed ${C.muted}`, background: '#fff', color: C.muted, fontSize: 13, cursor: 'pointer' },
  cantSeeActive: { background: C.panel, color: C.ink, borderStyle: 'solid' },
  note: { width: '100%', marginTop: 12, padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' },
  actions: { display: 'flex', justifyContent: 'space-between', marginTop: 16, marginBottom: 40 },
  btnGhost: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.muted, cursor: 'pointer', fontSize: 14 },
  btnPrimary: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 8, border: 'none', background: C.ink, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }
};

function scanLabel(status) {
  if (status === 'done') return 'Scan complete';
  if (status === 'stopped') return 'Stopped';
  if (status === 'error') return 'Scan error';
  return status;
}

export default ConditionLabeler;